import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import * as d3 from 'd3';
import { Chart } from 'react-google-charts';

import Constants from '../../../services/Constants';
import { formatCurrency } from '../../../services/NumberService';
import { changeDocumentsViewFilter } from '../../../actions';
import useGoogleChartTooltip from '../../../hooks/useGoogleChartTooltip';
import useD3Tooltip from '../../../hooks/useD3Tooltip';

const exposurePercentage = (d, total) => {
  const v = Math.round((d.exposure / total) * 1000) / 10;
  if (v.toString().indexOf('.') < 0) {
    return v + '.0';
  }
  return v;
};

let assetClassMapping = {};

const legendSize = 20;
const leftOffset = 20;
const legendSpacing = 3;

const colors = (assetClass) => {
  return assetClassMapping[assetClass]
    ? assetClassMapping[assetClass].color
    : Constants.ASSET_CLASS_COLORS.Overlay.color;
};

function arcTween(el, arc, outerRadius, delay) {
  d3.select(el)
    .transition()
    .delay(delay)
    .attrTween('d', function (d) {
      const i = d3.interpolate(d.outerRadius, outerRadius);
      return function (t) {
        d.outerRadius = i(t);
        return arc(d);
      };
    });
}

export function Landing(props) {
  const { data, liquidityData, print } = props;

  const landingChartRef = useRef(null);
  const { setChartContainerRef, followTooltip } = useGoogleChartTooltip();

  const getTotal = useCallback(() => {
    return data.reduce((tot, d) => tot + d.exposure, 0);
  }, [data]);

  const openManagers = useCallback((group) => {
    props.changeDocumentsViewFilter(Constants.PLAN_PORTFOLIO, { assetClass: group });
  }, [props.changeDocumentsViewFilter]);

  const { showTooltip: showD3Tooltip, hideTooltip } = useD3Tooltip(
    'landingPageChartTooltip'
  );

  const showTooltip = useCallback((d, total) => {
    const percent = exposurePercentage(d, total);
    showD3Tooltip(`
          <span>
            <p><strong>${d.group}</strong></p>
            <p>${formatCurrency(d.exposure)}</p>
            <p>${percent}%</p>
          </span>
          `);
  }, [showD3Tooltip]);

  const getChartOrder = (group) =>
    assetClassMapping[group]
      ? assetClassMapping[group].order
      : Constants.ASSET_CLASS_COLORS.Overlay.order;

  const drawImplRef = useRef(null);
  drawImplRef.current = () => {
    const parent = landingChartRef.current;
    if (!parent) return null;

    const container = parent.children[0];
    const { width: parentWidth } = parent.getBoundingClientRect();

    if (getTotal() <= 0) return null;

    const pieDiameter = print ? 380 : 345;
    const yTranslate = print ? pieDiameter / 2 - 30 : pieDiameter / 2;
    const legendWidth = 360;
    const legendHeight =
      (legendSize + legendSpacing) *
      (data.filter((d) => d.exposure !== 0).length + 2);

    let containerWidth = Math.min(parentWidth, pieDiameter + legendWidth + 100);
    const portraitMode = print ? true : containerWidth === parentWidth;
    containerWidth = portraitMode ? containerWidth - 300 : containerWidth;
    const containerHeight = print
      ? 780
      : portraitMode
        ? pieDiameter + legendHeight - 25
        : Math.max(pieDiameter, legendHeight);

    d3.select(container).selectAll('*').remove();

    const svg = d3
      .select(container)
      .attr('width', containerWidth - 130)
      .attr('height', containerHeight)
      .append('g')
      .attr('transform', 'translate(' + pieDiameter / 2 + ',' + yTranslate + ')');

    const total = data.reduce((tot, d) => tot + d.exposure, 0);

    drawPie(svg, data, total, pieDiameter, pieDiameter);

    const legendLeft = portraitMode ? pieDiameter * -0.5 : pieDiameter * 0.5;
    const legendTop = print
      ? pieDiameter * 0.43
      : portraitMode
        ? pieDiameter * 0.5
        : -150;
    drawLegend(svg, data, total, legendLeft, legendTop);
  };

  function drawPieLabels(arcs, outerRadius, labelInnerRadius, total) {
    const labelArc = d3
      .arc()
      .padRadius(outerRadius)
      .innerRadius(labelInnerRadius);

    arcs
      .append('text')
      .attr('transform', (d) => 'translate(' + labelArc.centroid(d) + ')')
      .style('fill', 'white')
      .attr('text-anchor', 'middle')
      .attr('font-size', '1.0em')
      .attr('pointer-events', 'none')
      .text((d) => {
        const percent = exposurePercentage(d.data, total);
        return parseInt(percent, 10) > 3 ? percent + '%' : '';
      });
  }

  function drawPie(svg, pieData, total, width, height) {
    if (getTotal() <= 0) return null;

    const outerRadius = Math.min(width, height) / 2 - 20;
    const innerRadius = 0;
    const labelInnerRadius = outerRadius / 2;
    const hoverRadius = 20;

    const arc = d3.arc().padRadius(outerRadius).innerRadius(innerRadius);
    const pie = d3.pie().value((d) => d.exposure);
    pie.sort((a, b) => getChartOrder(a.group) - getChartOrder(b.group));

    const chart = svg.selectAll('path').data(pie(pieData));
    const arcs = chart.enter();

    arcs
      .append('path')
      .each(function (d) {
        d.outerRadius = outerRadius - hoverRadius;
      })
      .attr('d', arc)
      .style('fill', (d, i) => colors(d.data.group, i))
      .on('mouseover', function (d) {
        arcTween(this, arc, outerRadius, 0);
        showTooltip(d.data, total);
      })
      .on('mousemove', (d) => showTooltip(d.data, total))
      .on('mouseout', function () {
        arcTween(this, arc, outerRadius - hoverRadius, 150);
        hideTooltip();
      });

    drawPieLabels(arcs, outerRadius, labelInnerRadius, total);
  }

  function drawLegendLabels(legendContainer, legendData, addEvents, xOffset, yOffset, total) {
    const labels = legendContainer.selectAll('text').data(legendData).enter();

    addEvents(
      labels
        .append('text')
        .attr('x', xOffset + legendSize / 2)
        .attr('y', (_d, i) => i * (legendSize + legendSpacing) + legendSize / 2 + 4 + yOffset)
        .attr('text-anchor', 'start')
        .attr('font-size', '0.8em')
        // .attr('cursor', 'pointer')
        .text((d) => d.group)
    );

    const assetXOffset = 270;

    addEvents(
      labels
        .append('text')
        .attr('x', leftOffset + xOffset + legendSize / 2 + assetXOffset)
        .attr('y', (_d, i) => i * (legendSize + legendSpacing) + legendSize / 2 + 4 + yOffset)
        .attr('text-anchor', 'end')
        .attr('font-size', '0.8em')
        // .attr('cursor', 'pointer')
        .text((d) => formatCurrency(d.exposure))
    );

    const totalYoffset =
      legendData.length * (legendSize + legendSpacing) + legendSize / 2 + 4 + yOffset;

    const resetFilter = () => openManagers('All');

    legendContainer
      .append('text')
      .attr('x', leftOffset + xOffset + legendSize / 2 + assetXOffset)
      .attr('y', totalYoffset)
      .attr('text-anchor', 'end')
      .attr('font-size', '0.8em')
      .text(formatCurrency(total))
      .on('click', resetFilter);

    legendContainer
      .append('text')
      .attr('x', xOffset + legendSize / 2)
      .attr('y', totalYoffset)
      .attr('text-anchor', 'start')
      .attr('font-size', '0.8em')
      .text('Total')
      .on('click', resetFilter);
  }

  function drawLegend(svg, allData, total, left, top) {
    if (getTotal() <= 0) return null;

    const sortedData = allData
      .filter((d) => d.exposure !== 0)
      .sort((a, b) => getChartOrder(a.group) - getChartOrder(b.group));

    const legendContainer = svg.append('g');
    const yOffset = top;
    const xOffset = leftOffset + left + legendSize * 2;

    const addEvents = (el) =>
      el.on('click', function (_d) {
        // const { group } = _d;
        // openManagers(group);
        // hideTooltip();
      });

    const legend = legendContainer.selectAll('rect').data(sortedData).enter();

    addEvents(
      legend
        .append('rect')
        .attr('x', xOffset - legendSize)
        .attr('y', (_d, i) => i * (legendSize + legendSpacing) + yOffset)
        .attr('width', legendSize)
        .attr('height', legendSize)
        .style('stroke', 'black')
        .style('stroke-width', '1')
        // .style('cursor', 'pointer')
        .style('fill', (d, i) => colors(d.group, i))
    );

    drawLegendLabels(legendContainer, sortedData, addEvents, xOffset, yOffset, total);
  }

  const draw = useMemo(() => _.throttle(() => drawImplRef.current(), 300), []);

  const redraw = useCallback(() => draw(), [draw]);

  useEffect(() => {
    window.addEventListener('resize', redraw);
    if (getTotal() > 0) redraw();
    return () => {
      window.removeEventListener('resize', redraw);
      draw.cancel();
      hideTooltip();
    };
  }, []);

  useEffect(() => {
    if (getTotal() > 0) redraw();
  });

  const orderedLiquidityData = [...(liquidityData || [])].sort(
    (a, b) => a.liquidityPk - b.liquidityPk
  );

  const headerRow = [
    'Name',
    ...orderedLiquidityData.flatMap((gp) => [
      `${gp.group} (${gp.percentage}%)`,
      { type: 'string', role: 'tooltip', p: { html: true } },
    ]),
  ];

  const dataRow = [
    'Liquidity',
    ...orderedLiquidityData.flatMap((gp) => {
      const tooltip =
        `<div class="liquidityTooltip">` +
        `<p class="liquidityTooltipTitle">${gp.group}</p>` +
        `<p>${formatCurrency(gp.liquidity)}</p>` +
        `<p>${gp.percentage}%</p>` +
        `</div>`;
      return [Number(gp.percentage), tooltip];
    }),
  ];
  
  const chartData = [headerRow, dataRow];

  const options = {
    chartArea: { width: 110, height: print ? '100%' : '90%' },
    colors: orderedLiquidityData.map((gp) => gp.color),
    isStacked: true,
    vAxis: {
      minValue: 0,
      maxValue: 100,
      format: "#'%'",
    },
    hAxis: {
      minValue: 0,
    },
    tooltip: { isHtml: true },
  };

  if (getTotal() <= 0) return null;

  return (
    <>
      <div className="landingPageChart" id="landing-page-chart" ref={landingChartRef}>
        <svg className={print ? 'printOnly' : 'noPrint'} />
        {!print && (
          <div
            id="reactgooglegraphToPDF"
            ref={setChartContainerRef}
            onMouseMove={followTooltip}
          >
            <Chart
              chartType="ColumnChart"
              width={'320px'}
              height={'330px'}
              data={chartData}
              options={options}
            />
          </div>
        )}
      </div>
      {!print && <h3>Portfolio Details</h3>}
    </>
  );
}

Landing.propTypes = {
  data: PropTypes.array,
  liquidityData: PropTypes.array,
  changeDocumentsViewFilter: PropTypes.func.isRequired,
  print: PropTypes.bool,
  printing: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const managers = state.resources.my_managers.data || [];
  const byAssetClass = _.groupBy(managers, 'assetType');
  const byLiquidityName = _.groupBy(managers, 'liquidityName');
  assetClassMapping = _.chain(managers)
    .keyBy('assetType')
    .mapValues((i) => ({
      color: i.assetTypeColor,
      order: i.assetTypeSortOrder,
    }))
    .value();
  const data = _.map(_.keys(byAssetClass), (group) => {
    const exposure = _.reduce(
      byAssetClass[group],
      (total, manager) => total + manager.assetValue,
      0
    );
    return { group, exposure };
  });
  const gsps = _.sum(_.map(managers, (manager) => manager.assetValue));
  const liquidityData = _.map(_.keys(byLiquidityName), (group) => {
    const liquidity = _.sum(
      _.map(byLiquidityName[group], (manager) => manager.assetValue)
    );
    const color = byLiquidityName[group][0].liquidityColor;
    const liquidityPk = byLiquidityName[group][0].liquidityPk;
    const percentage = Number((liquidity * 100) / gsps).toFixed(2);
    return { group, liquidity, color, liquidityPk, percentage };
  });
  return {
    data,
    liquidityData,
    printing: state.ui?.printing || false,
  };
};

export default connect(mapStateToProps, { changeDocumentsViewFilter })(Landing);
